import { useState, type MouseEvent } from 'react';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { Iconify } from '@/components';
import { getBookingSession } from '@/features/bookings-management/api';
import { openCustomDashboardChatRequest } from '@/features/custom-chat';
import type { ChatOpenChannelPayload } from '@/features/chat-stream/types';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';

import type { BookingSession } from '../../types';

type BookingSessionChatActionsProps = {
  bookingSession: BookingSession;
  compact?: boolean;
};

type ChatAction = 'client' | 'cleaner' | 'session';

const openChatRequest = (
  payload: ChatOpenChannelPayload,
  requestIdPrefix: string,
  requestIdSource: number | string
) => {
  openCustomDashboardChatRequest(payload, requestIdPrefix, requestIdSource);
};

export const BookingSessionChatActions = ({
  bookingSession,
  compact = false,
}: BookingSessionChatActionsProps) => {
  const queryClient = useQueryClient();
  const { errorMsg } = useSnackbarMsg();
  const chatAccess = useGetAccess('chat');
  const [openingAction, setOpeningAction] = useState<ChatAction | null>(null);
  const cleaners = bookingSession.booking_session_cleaners ?? [];
  const hasMultipleCleaners = cleaners.length > 1;

  if (!chatAccess.canView) {
    return null;
  }

  const resolveLatestBookingSession = async (): Promise<BookingSession> => {
    try {
      return await queryClient.fetchQuery<BookingSession>({
        queryKey: ['bookingSession', String(bookingSession.id)],
        queryFn: () => getBookingSession(bookingSession.id),
        staleTime: 0,
      });
    } catch {
      return bookingSession;
    }
  };

  const runChatAction = async (
    action: ChatAction,
    callback: () => Promise<void>
  ) => {
    if (openingAction) return;

    setOpeningAction(action);
    try {
      await callback();
    } catch (error) {
      errorMsg(error, 'Unable to open chat');
    } finally {
      setOpeningAction(null);
    }
  };

  const handleChatWithClient = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    void runChatAction('client', async () => {
      const session = await resolveLatestBookingSession();

      if (!session.user?.id) {
        errorMsg(
          new Error('No linked client was found for this session.'),
          'Unable to open client chat'
        );
        return;
      }

      openChatRequest(
        {
          client_user_id: Number(session.user.id),
          type: 'clientSupport',
        },
        'custom_client_chat',
        session.user.id
      );
    });
  };

  const handleChatWithCleaner = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    void runChatAction('cleaner', async () => {
      const session = await resolveLatestBookingSession();
      const resolvedCleaners = session.booking_session_cleaners ?? [];

      if (resolvedCleaners.length > 1) {
        openChatRequest(
          {
            booking_session_id: Number(session.id),
            type: 'jobSupport',
          },
          'custom_cleaners_chat',
          session.id
        );
        return;
      }

      const cleaner = resolvedCleaners[0];

      if (!cleaner?.id) {
        errorMsg(
          new Error('No assigned cleaner was found for this session.'),
          'Unable to open cleaner chat'
        );
        return;
      }

      openChatRequest(
        {
          cleaner_user_id: Number(cleaner.id),
          type: 'cleanerSupport',
        },
        'custom_cleaner_chat',
        cleaner.id
      );
    });
  };

  const handleViewSessionChat = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    void runChatAction('session', async () => {
      const session = await resolveLatestBookingSession();

      openChatRequest(
        {
          booking_session_id: Number(session.id),
          type: 'jobSupport',
        },
        'custom_session_chat',
        session.id
      );
    });
  };

  if (compact) {
    const cleanerChatLabel = hasMultipleCleaners
      ? 'Open cleaners chat'
      : 'Open cleaner chat';

    return (
      <Stack
        direction="row"
        spacing={0.25}
        alignItems="center"
        flexWrap="nowrap"
        onClick={(event) => event.stopPropagation()}
      >
        <Tooltip title="Open or create the v2 client support chat">
          <IconButton
            aria-label="Open client chat"
            disabled={!!openingAction}
            onClick={handleChatWithClient}
            size="small"
            color="primary"
          >
            <Iconify icon="eva:person-outline" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            hasMultipleCleaners
              ? 'Open or create the v2 shared session chat'
              : 'Open or create the v2 cleaner support chat'
          }
        >
          <IconButton
            aria-label={cleanerChatLabel}
            disabled={!!openingAction}
            onClick={handleChatWithCleaner}
            size="small"
            color="primary"
          >
            <Iconify icon="eva:people-outline" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open or create the v2 cleaning session chat">
          <IconButton
            aria-label="Open session chat"
            disabled={!!openingAction}
            onClick={handleViewSessionChat}
            size="small"
            color="primary"
          >
            <Iconify icon="eva:message-circle-outline" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      flexWrap="wrap"
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip title="Open or create the v2 client support chat">
        <Button
          disabled={!!openingAction}
          onClick={handleChatWithClient}
          size="small"
          startIcon={<Iconify icon="eva:person-outline" />}
          sx={{ px: 1, textTransform: 'none', whiteSpace: 'nowrap' }}
          variant="text"
        >
          Custom client
        </Button>
      </Tooltip>
      <Tooltip
        title={
          hasMultipleCleaners
            ? 'Open or create the v2 shared session chat'
            : 'Open or create the v2 cleaner support chat'
        }
      >
        <Button
          disabled={!!openingAction}
          onClick={handleChatWithCleaner}
          size="small"
          startIcon={<Iconify icon="eva:people-outline" />}
          sx={{ px: 1, textTransform: 'none', whiteSpace: 'nowrap' }}
          variant="text"
        >
          {hasMultipleCleaners ? 'Custom cleaners' : 'Custom cleaner'}
        </Button>
      </Tooltip>
      <Tooltip title="Open or create the v2 cleaning session chat">
        <Button
          color="primary"
          disabled={!!openingAction}
          onClick={handleViewSessionChat}
          size="small"
          startIcon={<Iconify icon="eva:message-circle-outline" />}
          sx={{ px: 1, textTransform: 'none', whiteSpace: 'nowrap' }}
          variant="text"
        >
          Custom session
        </Button>
      </Tooltip>
    </Stack>
  );
};
